// Low Stock Products Hook
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';

export interface LowStockProduct {
  id: string;
  name: string;
  code: string;
  stock_quantity: number;
  min_stock: number;
  sale_price: number;
  category?: string;
  updated_at: string;
}

export function useLowStockProducts(limit?: number) {
  const { company } = useAuthStore();

  return useQuery({
    queryKey: ['low-stock-products', company?.id, limit],
    queryFn: async () => {
      if (!company?.id) return [];

      // Fetch all active products
      const { data, error } = await supabase
        .from('products')
        .select('id, name, code, stock_quantity, min_stock_level, sale_price, category, updated_at')
        .eq('company_id', company.id)
        .eq('is_active', true)
        .order('stock_quantity', { ascending: true });

      if (error) {
        console.error('Low stock query error:', error);
        throw error;
      }

      // Filter products where stock_quantity <= min_stock_level
      const lowStockProducts = (data || []).filter(
        (product) => product.min_stock_level && product.stock_quantity <= product.min_stock_level
      );

      // Map min_stock_level to min_stock for consistency
      const mappedProducts = lowStockProducts.map(p => ({
        ...p,
        min_stock: p.min_stock_level
      }));

      // Apply limit if specified
      if (limit) {
        return mappedProducts.slice(0, limit) as LowStockProduct[];
      }

      return mappedProducts as LowStockProduct[];
    },
    enabled: !!company?.id,
    refetchInterval: 1000 * 60 * 5, // Refresh every 5 minutes
  });
}

// Get urgency level for styling
export function getStockUrgency(stockQty: number, minStock: number): 'critical' | 'low' | 'normal' {
  if (stockQty === 0) return 'critical';
  if (stockQty <= minStock * 0.5) return 'critical';
  if (stockQty <= minStock) return 'low';
  return 'normal';
}
